/**
 * CHAN DOAN FONT - rang buoc kho nhat cua ca san pham, hien thang len giao dien.
 *
 * Trang PDF khong luu "van ban", no luu lenh ve glyph. Font thuong duoc nhung
 * dang subset: chi chua dung nhung glyph da xuat hien. Neu subset chi co
 * 'b','o','l','d' thi khong the doi "bold" thanh "cold" - glyph 'c' khong ton tai.
 *
 * Module nay rut ra nhung gi CO THAT tu du lieu pdf.js dua ra:
 *   - ten PostScript va tien to subset 6 chu cai
 *   - font co duoc nhung khong (missingFile)
 *   - bit quyen nhung fsType, doc truc tiep tu bang OS/2 trong byte font
 *   - ToUnicode co hoat dong khong, suy ra tu chat luong van ban giai ma duoc
 *
 * Nhung gi loi Rust/PDFium se lam TOT HON sau nay: doc thang tu dictionary
 * /FontFile2 /FontFile3, doi chieu bang cmap that, va doi chieu voi font
 * he thong de biet co the tai tao subset hay khong.
 */
import type { FontDiagnostics, FontEditability } from '@pdfeditor/types';

/**
 * Hinh dang toi thieu cua doi tuong font ma pdf.js dat vao commonObjs.
 * pdf.js khong cong bo kieu cho lop nay nen ta mo ta lai o day thay vi dung any.
 */
export interface PdfJsFontObject {
  name?: string;
  loadedName?: string;
  /** Byte font da duoc pdf.js chuyen ve OpenType. Bang OS/2 duoc giu nguyen. */
  data?: Uint8Array | ArrayBuffer | null;
  mimetype?: string;
  /** true khi PDF khong nhung font (trinh xem phai the the font he thong). */
  missingFile?: boolean;
  isType3Font?: boolean;
  fallbackName?: string;
  vertical?: boolean;
}

/** Kho doi tuong dung chung cua pdf.js (page.commonObjs). */
export interface PdfJsObjectStore {
  has(id: string): boolean;
  get(id: string): unknown;
}

const SUBSET_PREFIX = /^[A-Z]{6}\+/;

/** Bit trong OS/2.fsType. Xem dac ta OpenType. */
const FSTYPE_RESTRICTED = 0x0002;
const FSTYPE_PREVIEW_PRINT = 0x0004;
const FSTYPE_EDITABLE = 0x0008;

/** 14 font chuan cua PDF - khong nhung cung luon co font he thong khop. */
const STANDARD_14 = [
  'courier', 'helvetica', 'times', 'symbol', 'zapfdingbats',
  'arial', 'timesnewroman', 'couriernew',
];

function toBytes(data: PdfJsFontObject['data']): Uint8Array | null {
  if (!data) return null;
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return null;
}

/**
 * Doc fsType tu bang OS/2 cua mot sfnt (TrueType/OpenType).
 * Tra ve null neu khong tim thay bang - font CFF thuan hoac Type1 co the khong co.
 */
export function readFsType(fontBytes: Uint8Array | null): number | null {
  if (!fontBytes || fontBytes.length < 12) return null;
  const view = new DataView(fontBytes.buffer, fontBytes.byteOffset, fontBytes.byteLength);

  let base = 0;
  const tag = view.getUint32(0, false);
  // 'ttcf' - tap hop font, lay font dau tien.
  if (tag === 0x74746366) {
    if (fontBytes.length < 16) return null;
    base = view.getUint32(12, false);
    if (base + 12 > fontBytes.length) return null;
  }

  const numTables = view.getUint16(base + 4, false);
  const dirStart = base + 12;
  if (dirStart + numTables * 16 > fontBytes.length) return null;

  for (let i = 0; i < numTables; i++) {
    const rec = dirStart + i * 16;
    // Tag 'OS/2' = 0x4F532F32
    if (view.getUint32(rec, false) === 0x4f532f32) {
      const off = view.getUint32(rec + 8, false);
      // fsType nam o byte thu 8 cua bang OS/2 (sau version, xAvgCharWidth,
      // usWeightClass, usWidthClass - moi truong 2 byte).
      if (off + 10 > fontBytes.length) return null;
      return view.getUint16(off + 8, false);
    }
  }
  return null;
}

export function embeddingAllowsEditing(fsType: number | null): boolean {
  // Khong doc duoc fsType: khong the ket luan la bi cam, coi nhu cho phep
  // nhung phan reason se noi ro la chua xac minh duoc.
  if (fsType === null) return true;
  if (fsType === 0) return true; // Installable Embedding
  if ((fsType & FSTYPE_RESTRICTED) !== 0) return false;
  if ((fsType & FSTYPE_EDITABLE) !== 0) return true;
  if ((fsType & FSTYPE_PREVIEW_PRINT) !== 0) return false;
  return true;
}

function isStandardFont(baseFont: string): boolean {
  const normalized = baseFont
    .replace(SUBSET_PREFIX, '')
    .replace(/[-,\s]/g, '')
    .toLowerCase();
  return STANDARD_14.some((s) => normalized.startsWith(s));
}

/**
 * Suy ra ToUnicode co dung duoc khong tu chinh van ban pdf.js giai ma ra.
 *
 * pdf.js da ap dung /ToUnicode neu co. Neu ket qua chi toan ky tu vung dung
 * rieng (U+E000..U+F8FF) hoac toan U+FFFD thi anh xa Unicode that su hong -
 * do la dau hieu tin cay hon la nhin vao su ton tai cua entry ToUnicode.
 */
export function looksDecodable(sample: string): boolean {
  const chars = [...sample].filter((c) => c.trim().length > 0);
  if (chars.length === 0) return true; // khong co du lieu de ket luan
  let bad = 0;
  for (const c of chars) {
    const cp = c.codePointAt(0) ?? 0;
    const privateUse = cp >= 0xe000 && cp <= 0xf8ff;
    const replacement = cp === 0xfffd;
    const control = cp < 0x20 && cp !== 0x09 && cp !== 0x0a;
    if (privateUse || replacement || control) bad++;
  }
  return bad / chars.length < 0.3;
}

/**
 * NANG LUC THAT CUA LOI DANG CHAY.
 *
 * "Sua duoc hay khong" khong phai thuoc tinh cua rieng font - no la thuoc tinh
 * cua CAP (font, loi). Truoc khi co font dong goi, mot font subset thieu glyph
 * tieng Viet la ngo cut that su: khong co nguon glyph nao khac. Bay gio da co
 * Noto Sans (OFL-1.1) di kem ung dung, nen cung mot font do lai la 'resubset'.
 *
 * Bo cau truc nay ton tai de cau tra loi doi theo nang luc - neu khong, giao
 * dien se tiep tuc bao "khong sua duoc" cho nhung tai lieu MA BAY GIO DA SUA
 * DUOC, va do dung la loi ma nguoi dung vua gap.
 */
export interface CoreCapabilities {
  /**
   * Loi co font du phong phu tieng Viet de thay the hay khong.
   * false doi voi ban gia lap chi doc.
   */
  bundledFontAvailable: boolean;
}

const NO_CAPABILITIES: CoreCapabilities = { bundledFontAvailable: false };

function decideEditability(input: {
  embedded: boolean;
  isSubset: boolean;
  hasToUnicode: boolean;
  allowsEditing: boolean;
  standard: boolean;
  isType3: boolean;
  caps: CoreCapabilities;
}): { editability: FontEditability; reason: string } {
  const { embedded, isSubset, hasToUnicode, allowsEditing, standard, isType3, caps } = input;
  const bundled = caps.bundledFontAvailable;

  if (isType3) {
    return {
      editability: 'none',
      reason:
        'Font Type 3: glyph duoc dinh nghia bang thu tuc ve rieng, khong co anh xa ky tu on dinh. Khong the sua noi dung, chi co the che hoac ve de len.',
    };
  }
  if (!hasToUnicode) {
    return {
      editability: 'none',
      reason:
        'Thieu bang anh xa /ToUnicode dung dan: khong khoi phuc duoc ky tu Unicode tu ma glyph. Sua van ban se cho ra ky tu sai. Can OCR lai vung nay.',
    };
  }
  if (!allowsEditing) {
    // fsType cam SU DUNG LAI FONT NAY - no khong cam dung mot font khac.
    // Do tren 284 tai lieu that: fsType chan font nhung san tren 74 tai lieu.
    // Voi font dong goi, nhung tai lieu do van sua duoc, chi la doi hinh thuc.
    if (bundled) {
      return {
        editability: 'resubset',
        reason:
          'Bit quyen fsType cua font cam nhung de chinh sua (chi cho xem va in), nen KHONG duoc ' +
          'dung lai font nay. Van sua duoc bang font dong goi Noto Sans (giay phep OFL-1.1 cho ' +
          'phep ca nhung ca phan phoi lai). HINH THUC CHU SE DOI - se hoi lai truoc khi luu.',
      };
    }
    return {
      editability: 'style-only',
      reason:
        'Bit quyen fsType cua font cam nhung de chinh sua (chi cho xem va in). Chi doi duoc mau chu va co chu; doi ky tu se vi pham giay phep font.',
    };
  }
  if (!embedded) {
    return {
      editability: standard ? 'full' : 'resubset',
      reason: standard
        ? 'Font khong nhung nhung thuoc bo 14 font chuan cua PDF: luon co font he thong khop. Sua thoai mai.'
        : 'Font khong nhung. Se dung font he thong gan nhat khi sua - hinh dang chu co the lech nhe so voi ban goc.',
    };
  }
  if (!isSubset) {
    return {
      editability: 'full',
      reason: 'Font duoc nhung day du, con nguyen bo glyph. Them ky tu moi khong gap tro ngai.',
    };
  }
  return {
    editability: 'resubset',
    reason: bundled
      ? 'Font duoc nhung dang subset (chi chua glyph da dung). Do phu tieng Viet cua font subset ' +
        'thuong bang 0, nen ky tu moi rat co the phai lay tu font dong goi Noto Sans - luc do ' +
        'hinh thuc chu se doi va se co canh bao truoc khi luu.'
      : 'Font duoc nhung dang subset (chi chua glyph da dung). Sua duoc, nhung ky tu moi nam ngoai subset phai lay tu font goc va nhung bo sung khi luu.',
  };
}

/**
 * Dung ban chan doan cho mot font ma pdf.js da nap.
 * fontKey la ten trong text item (vd "g_d0_f1"), duoc tra ve tu getTextContent().
 */
export function diagnoseFont(
  commonObjs: PdfJsObjectStore,
  fontKey: string,
  declaredName: string,
  textSample: string,
  caps: CoreCapabilities = NO_CAPABILITIES,
): FontDiagnostics {
  let font: PdfJsFontObject | null = null;
  try {
    if (commonObjs.has(fontKey)) {
      font = commonObjs.get(fontKey) as PdfJsFontObject;
    }
  } catch {
    // commonObjs.get nem loi neu doi tuong chua duoc giai quyet. Bo qua:
    // ta van con ten khai bao tu bang styles cua getTextContent().
    font = null;
  }

  const baseFont = font?.name || declaredName || fontKey;
  const isSubset = SUBSET_PREFIX.test(baseFont);
  const isType3 = font?.isType3Font === true;
  const bytes = toBytes(font?.data ?? null);
  // missingFile = pdf.js khong tim thay du lieu font nhung trong file.
  const embedded = font ? font.missingFile !== true : bytes !== null;
  const fsType = readFsType(bytes);
  const allowsEditing = embeddingAllowsEditing(fsType);
  const hasToUnicode = looksDecodable(textSample);
  const standard = isStandardFont(baseFont);

  const { editability, reason } = decideEditability({
    embedded,
    isSubset,
    hasToUnicode,
    allowsEditing,
    standard,
    isType3,
    caps,
  });

  const fsNote =
    fsType === null
      ? ' (Khong doc duoc bang OS/2 nen chua xac minh duoc quyen nhung font.)'
      : '';

  return {
    baseFont,
    isSubset,
    embedded,
    hasToUnicode,
    fsType,
    embeddingAllowsEditing: allowsEditing,
    editability,
    reason: reason + fsNote,
  };
}

/** Thu tu tu tot den kem. Chan doan gop cua ca khoi lay muc KEM NHAT. */
const RANK: Record<FontEditability, number> = {
  full: 0,
  resubset: 1,
  'style-only': 2,
  none: 3,
};

export function worstEditability(list: readonly FontEditability[]): FontEditability {
  let worst: FontEditability = 'full';
  for (const e of list) {
    if (RANK[e] > RANK[worst]) worst = e;
  }
  return worst;
}

export const EDITABILITY_LABEL: Record<FontEditability, string> = {
  full: 'Sửa được hoàn toàn',
  resubset: 'Sửa được, cần nhúng lại font',
  'style-only': 'Chỉ đổi được kiểu chữ',
  none: 'Không sửa được',
};
